import React, { useState, useEffect } from 'react';
import { Shield, X, Smartphone, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { logger } from '../../utils/logger';

const TwoFactorModal = ({ email, userId, twoFactorMethod, onSuccess, onCancel }) => {
  const { finalizeTwoFactorLogin } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  // Envoyer le code par email si méthode email
  useEffect(() => {
    if (twoFactorMethod === 'email' && !codeSent) {
      sendEmailCode();
    }
  }, [twoFactorMethod, codeSent]);

  const sendEmailCode = async () => {
    try {
      logger.log('📧 Envoi du code 2FA par email...');
      const send2FACodeFunction = httpsCallable(functions, 'send2FACode');
      await send2FACodeFunction({ userId });
      setCodeSent(true);
      logger.log('✅ Code 2FA envoyé par email');
    } catch (error) {
      logger.error('❌ Erreur envoi code 2FA:', error);
      setError('Erreur lors de l\'envoi du code. Veuillez réessayer.');
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      logger.log('🔐 Vérification du code 2FA...');
      
      // Appeler la Cloud Function pour vérifier le code
      const verify2FACodeFunction = httpsCallable(functions, 'verify2FACode');
      const result = await verify2FACodeFunction({ 
        userId, 
        code 
      });

      if (result.data.success) {
        logger.log('✅ Code 2FA vérifié avec succès');
        
        // Finaliser la connexion dans AuthContext
        await finalizeTwoFactorLogin(userId);
        
        onSuccess();
      } else {
        throw new Error(result.data.error || 'Code invalide');
      }
      
    } catch (error) {
      logger.error('❌ Erreur vérification 2FA:', error);
      setError(error.message || 'Code invalide. Veuillez réessayer.');
      setCode('');
    }
    
    setIsLoading(false);
  };

  const handleResendCode = async () => {
    if (twoFactorMethod === 'email') {
      setCodeSent(false);
      setError('');
      setCode('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Shield className="text-blue-600" size={24} />
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">
              Authentification à deux facteurs
            </h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Message selon la méthode */}
        <div className="mb-4">
          {twoFactorMethod === 'email' ? (
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Mail className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-semibold mb-1">Code envoyé par email</p>
                <p>Un code de vérification a été envoyé à <strong>{email}</strong></p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Smartphone className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-semibold mb-1">Application d'authentification</p>
                <p>Entrez le code de votre application (Google Authenticator, Authy, etc.)</p>
              </div>
            </div>
          )}
        </div>

        {/* Code input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Code de vérification
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            maxLength={6}
            className="w-full text-center text-2xl tracking-widest border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter' && code.length === 6) {
                handleVerify();
              }
            }}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 rounded-lg">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleVerify}
            isLoading={isLoading}
            disabled={isLoading || code.length !== 6}
            className="flex-1"
          >
            Vérifier
          </Button>
          <Button onClick={onCancel} variant="outline">
            Annuler
          </Button>
        </div>

        {/* Resend code (email only) */}
        {twoFactorMethod === 'email' && (
          <div className="mt-4 text-center">
            <button
              onClick={handleResendCode}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              disabled={isLoading}
            >
              Renvoyer le code
            </button>
          </div>
        )}

        <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
          {twoFactorMethod === 'email' 
            ? 'Le code expire dans 10 minutes' 
            : 'Le code change toutes les 30 secondes'}
        </p>
      </div>
    </div>
  );
};

export default TwoFactorModal;